import React, { Dispatch, SetStateAction, useState } from "react"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputTaxJarConfig } from "lib/types/generated/graphql-types"
import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { PROVIDER_CATEGORY, PROVIDER_TYPE } from "lib/types/common"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import DeleteButton from "components/Ui/EditSidebar/DeleteButton"
import ConnectedStoreMarket from "components/Config/ConnectedStoreMarkets/ConnectedStoreMarket"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import Popup from "components/Ui/Popup"
import { GET_TAX_JAR_CONFIG } from "../../../../graphql/queries/config/Providers/TaxJar/GetTaxJarConfig"
import { CREATE_OR_UPDATE_TAX_JAR_CONFIG } from "../../../../graphql/mutations/config/Providers/TaxJar/CreateOrUpdateTaxJarConfig"
import { VALIDATE_TAX_JAR } from "../../../../graphql/queries/config/Providers/TaxJar/ValidateTaxJarConfig"
import { DELETE_TAX_JAR_CONFIG } from "../../../../graphql/mutations/config/Providers/TaxJar/DeleteTaxJarConfig"
import { TaxJarConfigInputs } from "../../../../components/Config/TaxJar/TaxJarConfigInputs"
import { handleErrorMessages } from "helpers/errors"
import { WithAuth } from "../../../../components/Auth"
import { PermissionGroups } from "../../../../routes/constants"

type Props = {
  refetch: () => void
  editId: string
  setEditId: Dispatch<SetStateAction<string>>
}

interface ExtendedInputTaxJarConfig {
  id: string
  taxJarConfig: InputTaxJarConfig
}

export const EditTaxJarConfig = ({ refetch, editId, setEditId }: Props) => {
  const [validationError, setValidationError] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const { data } = useQuery(GET_TAX_JAR_CONFIG, {
    variables: { id: editId }
  })
  const closeSidebar = () => {
    setEditId("")
    dispatch(hideEditSidebar())
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ExtendedInputTaxJarConfig>({ mode: "onBlur" })

  const [updateTaxJarConfig, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_TAX_JAR_CONFIG,
    {
      onCompleted: () => {
        dispatch(hideEditSidebar())
        reset(taxJarConfig)
        dispatch(
          alertActions.actions.createAlert({
            message: "TaxJar configuration successfully updated",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )
  const taxJarConfig = data?.getTaxJarConfig
  const { id } = taxJarConfig

  const [validateTaxJar, { loading }] = useLazyQuery(VALIDATE_TAX_JAR, {
    onError: (error) => handleErrorMessages(error)
  })

  const onSubmit: SubmitHandler<ExtendedInputTaxJarConfig> = (data) => {
    const { baseUrl, apiKey } = data.taxJarConfig
    validateTaxJar({
      variables: {
        input: {
          baseUrl,
          apiKey
        }
      }
    }).then((response) => {
      if (response.data.Configuration.validate.taxJar.valid) {
        updateTaxJarConfig({
          variables: {
            id: editId,
            taxJarConfig: {
              baseUrl,
              apiKey
            }
          }
        })
      } else {
        setValidationError(true)
      }
    })
  }

  if (!taxJarConfig) return null

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar cancelEvent={() => closeSidebar()}>
        <EditSidebarHeader title={id} id={id} cancelEvent={() => closeSidebar()}>
          <WithAuth requiredGroups={PermissionGroups.providers.write}>
            <PrimaryButton type="submit" loading={createLoading || loading}>
              Save
            </PrimaryButton>
            <DeleteButton
              id={id}
              name={id}
              entity={"TaxJar configuration"}
              redirectUrl="/settings/providers/tax-jar"
              mutation={DELETE_TAX_JAR_CONFIG}
              refetch={refetch}
              postDeleteFunction={() => setEditId("")}
              secureDelete={true}
            />
          </WithAuth>
        </EditSidebarHeader>
        {validationError ? (
          <Popup
            title="Validation error"
            subtitle="Couldn't validate provided credentials with TaxJar"
            hideOkButton={true}
            cancelButtonText={"Close"}
            handleCloseClick={() => setValidationError(false)}
          />
        ) : (
          <></>
        )}
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Connected store markets</Tab>
          </TabList>
          <TabPanel>
            <TaxJarConfigInputs
              control={control}
              errors={errors}
              defaultValues={taxJarConfig}
              showId={false}
            />
          </TabPanel>
          <TabPanel>
            <ConnectedStoreMarket
              providerId={taxJarConfig.id}
              providerType={PROVIDER_TYPE.TAX_JAR}
              providerCategory={PROVIDER_CATEGORY.TAX}
            />
          </TabPanel>
        </Tabs>
      </EditSidebar>
    </form>
  )
}
